// src/components/Layout.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import { collection, getDocs, query, where, limit } from "firebase/firestore";
import { auth, db } from "../firebase";
import { useTranslation } from "react-i18next";
import { SCHOOL } from "../config";
import { useTheme } from "../context/ThemeContext";

export default function Layout({ children, title, student }) {
  const navigate     = useNavigate();
  const location     = useLocation();
  const { t }        = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { dark, toggle: toggleTheme } = useTheme();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!student) return;
    let cancelled = false;
    async function fetchUnread() {
      try {
        const lastCheck = parseInt(localStorage.getItem(`notifCheck_${student.id}`) || "0");
        const lastCheckIso = new Date(lastCheck).toISOString();
        const snap = await getDocs(
          query(collection(db, "notifications"),
            where("createdAt", ">", lastCheckIso),
            limit(50))
        );
        const count = snap.docs.filter(d => {
          const n = d.data();
          return (
            n.target === "all" ||
            (n.target === "batch"   && n.batchId   === student.batchId) ||
            (n.target === "student" && n.studentId === student.id)
          );
        }).length;
        if (!cancelled) setUnreadCount(count);
      } catch { /* ignore */ }
    }
    fetchUnread();
    return () => { cancelled = true; };
  }, [student]);

  const NAV = [
    { path:"/",        icon:"🏠", label: t("nav.dashboard") },
    { path:"/course",  icon:"🎵", label: t("nav.myCourse")  },
    { path:"/library", icon:"📚", label: t("nav.library")   },
    { path:"/fees",    icon:"💰", label: t("nav.fees")      },
    { path:"/profile", icon:"👤", label: t("nav.profile")   },
  ];

  function isActive(path) {
    if (path === "/course") return location.pathname.startsWith("/course");
    return location.pathname === path;
  }

  function go(path) {
    if (path === "/course") {
      if (student?.courseId)
        navigate(`/course/${student.courseId}`,
          { state: { batchId: student.batchId || "", isCompleted: false }, replace: true });
      else navigate("/", { replace: true });
    } else {
      navigate(path, { replace: true });
    }
    setMobileOpen(false);
  }

  async function logout() {
    await signOut(auth);
    navigate("/login");
  }

  return (
    <div className="app-layout">

      {/* Mobile overlay */}
      {mobileOpen && (
        <div onClick={()=>setMobileOpen(false)}
          style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.4)",
                   zIndex:99 }}/>
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${mobileOpen?"open":""}`}>
        <div className="sidebar-logo">
          <picture>
            <source srcSet="/logo.webp" type="image/webp" />
            <img src="/logo.png" alt={SCHOOL.name}
              style={{ width:"72px", height:"72px", objectFit:"contain",
                       borderRadius:"10px", marginBottom:"10px" }}/>
          </picture>
          <h1 style={{ fontSize:".95rem", lineHeight:1.3 }}>{SCHOOL.name}</h1>
          <p style={{ fontSize:".72rem", opacity:.75, marginTop:"2px" }}>
            {SCHOOL.tagline}
          </p>
        </div>

        {/* Student mini card */}
        {student && (
          <div style={{ padding:"12px 16px",
                        borderBottom:"1px solid var(--sb-border)",
                        background:"var(--sb-stu-bg)" }}>
            <div style={{ display:"flex",alignItems:"center",gap:"10px" }}>
              <div style={{ width:"36px",height:"36px",borderRadius:"50%",
                            background:"var(--sb-avatar-bg)",border:"2px solid var(--gold)",
                            display:"flex",alignItems:"center",
                            justifyContent:"center",fontSize:"1rem",
                            flexShrink:0,overflow:"hidden" }}>
                {student.photoUrl
                  ? <img src={student.photoUrl} alt="" loading="lazy"
                      style={{ width:"100%",height:"100%",objectFit:"cover" }}/>
                  : "👤"}
              </div>
              <div style={{ overflow:"hidden" }}>
                <div style={{ color:"var(--sb-text-hi)",fontWeight:"700",
                              fontSize:".86rem",
                              whiteSpace:"nowrap",overflow:"hidden",
                              textOverflow:"ellipsis" }}>
                  {student.name}
                </div>
                <div style={{ color:"var(--gold)",fontSize:".72rem",fontWeight:"600" }}>
                  {t("nav.student")}
                </div>
              </div>
            </div>
          </div>
        )}

        <nav className="sidebar-nav">
          {NAV.map(n => (
            <button key={n.path}
              className={`nav-item ${isActive(n.path)?"active":""}`}
              onClick={() => go(n.path)}>
              <span className="nav-icon">{n.icon}</span>
              {n.label}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button onClick={logout}
            style={{ background:"var(--sb-stu-bg)",border:"1px solid var(--sb-border)",
                     color:"var(--purple-text)",padding:"7px 14px",
                     borderRadius:"8px",cursor:"pointer",
                     fontSize:".82rem",fontWeight:"600",
                     width:"100%",marginBottom:"10px" }}>
            🚪 {t("nav.logout")}
          </button>
          <div style={{ color:"var(--purple-text)",fontWeight:"600" }}>{SCHOOL.teacher}</div>
          <div>📞 {SCHOOL.phone}</div>
          <div>📧 {SCHOOL.email}</div>
          <div style={{ marginTop:"4px" }}>{SCHOOL.address}</div>
        </div>
      </aside>

      {/* Main */}
      <div className="main-content">
        {/* Top header */}
        <header className="top-header">
          {/* Mobile hamburger */}
          <button onClick={()=>setMobileOpen(!mobileOpen)}
            style={{ display:"none",background:"none",border:"none",
                     fontSize:"1.4rem",cursor:"pointer",
                     color:"var(--text-label)" }}
            className="hamburger">
            ☰
          </button>

          <h2>{title}</h2>

          {/* Header right */}
          <div style={{ display:"flex",alignItems:"center",gap:"10px" }}>
            {student && (
              <span style={{ fontSize:".85rem",color:"var(--text-muted)",
                             whiteSpace:"nowrap" }}>
                {t("common.welcome")},{" "}
                <strong style={{ color:"var(--purple-text)" }}>
                  {student.name?.split(" ")[0]}
                </strong>
              </span>
            )}
            {/* Notifications bell */}
            <button
              onClick={() => navigate("/notifications")}
              title="Notifications"
              style={{
                position:"relative",
                background:"var(--input-bg)", border:"1px solid var(--border-warm)",
                color:"var(--sb-text-hi)", borderRadius:"8px",
                width:"34px", height:"34px", cursor:"pointer",
                fontSize:"1rem", display:"flex", alignItems:"center",
                justifyContent:"center", flexShrink:0,
              }}>
              🔔
              {unreadCount > 0 && (
                <span style={{
                  position:"absolute", top:"-5px", right:"-5px",
                  background:"#EF4444", color:"white",
                  borderRadius:"50%", minWidth:"16px", height:"16px",
                  fontSize:".65rem", fontWeight:"800",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  lineHeight:1, padding:"0 3px",
                }}>
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              title={dark ? "Switch to light theme" : "Switch to dark theme"}
              style={{
                background:"var(--input-bg)", border:"1px solid var(--border-warm)",
                color:"var(--sb-text-hi)", borderRadius:"8px",
                width:"34px", height:"34px", cursor:"pointer",
                fontSize:"1rem", display:"flex", alignItems:"center",
                justifyContent:"center", flexShrink:0,
              }}>
              {dark ? "☀️" : "🌙"}
            </button>
            <div className="header-avatar"
              onClick={()=>navigate("/profile")}>
              {student?.photoUrl
                ? <img src={student.photoUrl} alt="" loading="lazy"/>
                : "👤"}
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="page-body">
          {children}
        </div>
      </div>

      {/* Bottom nav bar (mobile) */}
      <nav className="bottom-nav-bar">
        {NAV.map(n => (
          <button key={n.path}
            className={`bnav-btn ${isActive(n.path) ? "active" : ""}`}
            onClick={() => go(n.path)}>
            <span className="bnav-icon">{n.icon}</span>
            {n.label}
          </button>
        ))}
      </nav>

    </div>
  );
}
